import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent,
  type RefObject,
  type TouchEvent,
} from "react";
import { useAddress } from "@/shared/hooks/useAddress";
import { signatureService } from "@/features/protest/services/signature.services";
import { propertyService } from "@/features/properties/model/propertyServices";

/* -------------------------------------------------------------------------- */
/*                         Internal types & API helpers                        */
/* -------------------------------------------------------------------------- */

type ContinuePropertyType = "residential" | "commercial" | "RESIDENTIAL" | "COMMERCIAL";

const DOC_DRIVING_LICENCE = "driving_licence";

export type PropertyRelationshipSubmission =
  | {
      type: "owner";
    }
  | {
      type: "family";
      relationshipToOwner: string;
      reasonForFiling: string;
      drivingLicenseFile: File | null;
    };

const getPropertyType = (type: string, rawType: string): "residential" | "commercial" => {
  const normalizedType = (type || "").toLowerCase();
  const normalizedRawType = (rawType || "").toLowerCase();

  if (normalizedRawType === "sfr" || normalizedType === "residential") {
    return "residential";
  }
  if (normalizedRawType === "mfr" || normalizedType === "commercial") {
    return "commercial";
  }
  return "commercial";
};

type KycCompleteInput = {
  isCommercial: boolean;
  lastNameVerificationOk: boolean;
  isLastNameVerifyFailed: boolean;
  drivingLicenseFile: File | null;
  formationFile: File | null;
  authorizationFile: File | null;
};

function isKycComplete(input: KycCompleteInput): boolean {
  const {
    isCommercial,
    lastNameVerificationOk,
    isLastNameVerifyFailed,
    drivingLicenseFile,
    formationFile,
    authorizationFile,
  } = input;
  if (isCommercial) {
    return Boolean(formationFile && authorizationFile);
  }
  return lastNameVerificationOk || (isLastNameVerifyFailed && Boolean(drivingLicenseFile));
}

async function verifyOwnerLastName(propertyId: string, lastName: string): Promise<boolean> {
  const normalized = lastName.trim();
  if (!propertyId.trim()) {
    throw new Error("Please select a property address before verifying.");
  }
  if (!normalized) {
    throw new Error("Please enter your last name.");
  }
  const res = await signatureService.verifyLastName({
    property_id: propertyId,
    last_name: normalized,
  });
  return Boolean(res.success && res.data?.is_valid);
}

type SubmitContinueModalInput = {
  propertyId: string;
  selectedPropertyType: ContinuePropertyType;
  signature: string;
  propertyRelationship: PropertyRelationshipSubmission;
};

type ValidFamilyMemberSubmission = {
  type: "family";
  relationshipToOwner: string;
  reasonForFiling: string;
  drivingLicenseFile: File;
};

function validateFamilyMemberSubmission(
  propertyRelationship: PropertyRelationshipSubmission
): ValidFamilyMemberSubmission | null {
  if (propertyRelationship.type !== "family") return null;

  if (!propertyRelationship.relationshipToOwner.trim()) {
    throw new Error("Please select your relationship to the owner.");
  }
  if (!propertyRelationship.reasonForFiling.trim()) {
    throw new Error("Please enter the reason for filing on their behalf.");
  }
  if (!propertyRelationship.drivingLicenseFile) {
    throw new Error("Please upload your driving license.");
  }

  return {
    type: "family",
    relationshipToOwner: propertyRelationship.relationshipToOwner,
    reasonForFiling: propertyRelationship.reasonForFiling,
    drivingLicenseFile: propertyRelationship.drivingLicenseFile,
  };
}

async function submitContinueModalFlow(input: SubmitContinueModalInput): Promise<void> {
  const { propertyId, selectedPropertyType, signature, propertyRelationship } = input;

  if (!propertyId.trim()) {
    throw new Error("Please select a property address before continuing.");
  }
  if (!signature) {
    return;
  }

  const familyMemberSubmission = validateFamilyMemberSubmission(propertyRelationship);
  if (familyMemberSubmission) {
    const detailsRes = await propertyService.updateDetails(propertyId, {
      property_user_type: "familymember",
      relationshipToOwner: familyMemberSubmission.relationshipToOwner.trim(),
      reasonForFiling: familyMemberSubmission.reasonForFiling.trim(),
    });
    const detailsMessage = (detailsRes.message || "").toLowerCase();
    const detailsLooksSuccessful = detailsMessage.includes("success");
    if (!detailsRes.success && !detailsLooksSuccessful) {
      throw new Error(detailsRes.message || "Could not update property relationship details.");
    }

    const uploadRes = await signatureService.uploadKYCFile({
      property_id: propertyId,
      document_type: DOC_DRIVING_LICENCE,
      file: familyMemberSubmission.drivingLicenseFile,
    });
    const uploadMessage = (uploadRes.message || "").toLowerCase();
    const uploadLooksSuccessful = uploadMessage.includes("success");
    if (
      !uploadRes.success &&
      !uploadLooksSuccessful &&
      !uploadRes.data?.url &&
      !uploadRes.data?.driving_licence_url
    ) {
      throw new Error(uploadRes.message || "Driving license upload failed.");
    }
  }

  const signRes = await signatureService.submitSignature({
    property_type: selectedPropertyType,
    property_id: propertyId,
    signature,
  });
  const signMessage = (signRes.message || "").toLowerCase();
  const signLooksSuccessful = signMessage.includes("success");
  if (!signRes.success && !signLooksSuccessful) {
    throw new Error(signRes.message || "Signature submission failed.");
  }
}

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

export type UseSignatureContinueModalParams = {
  isOpen: boolean;
  selectedPropertyType: ContinuePropertyType | null;
  propertyRelationship?: PropertyRelationshipSubmission;
  onClose: () => void;
  onSubmit?: () => void;
};

export type UseSignatureContinueModalResult = {
  visible: boolean;
  propertyId: string;
  isCommercial: boolean;
  lastName: string;
  setLastName: (value: string) => void;
  isLastNameVerifyFailed: boolean;
  isVerifyingLastName: boolean;
  onVerifyLastName: () => void;
  drivingLicenseFileName: string;
  onDrivingLicenseChange: (event: ChangeEvent<HTMLInputElement>) => void;
  formationFileName: string;
  onFormationChange: (event: ChangeEvent<HTMLInputElement>) => void;
  authorizationFileName: string;
  onAuthorizationChange: (event: ChangeEvent<HTMLInputElement>) => void;
  signatureCanvasRef: RefObject<HTMLCanvasElement>;
  startDrawing: (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => void;
  draw: (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => void;
  stopDrawing: () => void;
  clearSignature: () => void;
  signature: string | null;
  kycComplete: boolean;
  isSubmitting: boolean;
  isSuccessModalOpen: boolean;
  successRedirectSeconds: number;
  signatureConsentAccepted: boolean;
  setSignatureConsentAccepted: (value: boolean) => void;
  handleSubmit: () => Promise<void>;
  onSuccessContinue: () => void;
  onClose: () => void;
};

export function useSignatureContinueModal({
  isOpen,
  selectedPropertyType,
  propertyRelationship = { type: "owner" },
  onClose,
  onSubmit,
}: UseSignatureContinueModalParams): UseSignatureContinueModalResult {
  const { selectedAddress: propertyId } = useAddress();
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [lastName, setLastName] = useState("");
  const [isLastNameVerifyFailed, setIsLastNameVerifyFailed] = useState(false);
  const [lastNameVerificationOk, setLastNameVerificationOk] = useState(false);
  const [isVerifyingLastName, setIsVerifyingLastName] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successRedirectSeconds, setSuccessRedirectSeconds] = useState(7);
  const [drivingLicenseFileName, setDrivingLicenseFileName] = useState("");
  const [drivingLicenseFile, setDrivingLicenseFile] = useState<File | null>(null);
  const [formationFileName, setFormationFileName] = useState("");
  const [formationFile, setFormationFile] = useState<File | null>(null);
  const [authorizationFileName, setAuthorizationFileName] = useState("");
  const [authorizationFile, setAuthorizationFile] = useState<File | null>(null);
  const [signatureConsentAccepted, setSignatureConsentAccepted] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setLastName("");
    setIsLastNameVerifyFailed(false);
    setLastNameVerificationOk(false);
    setDrivingLicenseFileName("");
    setDrivingLicenseFile(null);
    setFormationFileName("");
    setFormationFile(null);
    setAuthorizationFileName("");
    setAuthorizationFile(null);
    setSignature(null);
    setSignatureConsentAccepted(false);
    setIsSuccessModalOpen(false);
    setSuccessRedirectSeconds(7);
  }, [isOpen]);

  const redirectToSupportingUpload = useCallback(() => {
    onSubmit?.();
    onClose();
  }, [onSubmit, onClose]);

  useEffect(() => {
    if (!isOpen || !isSuccessModalOpen) return;
    if (successRedirectSeconds <= 0) return;

    const intervalId = window.setInterval(() => {
      setSuccessRedirectSeconds((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isOpen, isSuccessModalOpen, successRedirectSeconds]);

  useEffect(() => {
    if (!isOpen || !isSuccessModalOpen) return;

    const timeoutId = window.setTimeout(() => {
      redirectToSupportingUpload();
    }, 7000);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, isSuccessModalOpen, redirectToSupportingUpload]);

  useEffect(() => {
    if (!isOpen) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(rect.width * devicePixelRatio);
    canvas.height = Math.floor(rect.height * devicePixelRatio);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(devicePixelRatio, devicePixelRatio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#111827";
  }, [isOpen]);

  const getCoordinates = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();

    let clientX: number;
    let clientY: number;
    if ("touches" in e && e.touches.length > 0) {
      const touch = e.touches.item(0);
      if (!touch) return null;
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else if ("clientX" in e) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      return null;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDrawing = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setSignature(canvas.toDataURL());
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = signatureCanvasRef.current;
      if (canvas) setSignature(canvas.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };

  const visible = isOpen && selectedPropertyType !== null;
  const normalizedPropertyType = selectedPropertyType
    ? getPropertyType(selectedPropertyType, selectedPropertyType)
    : null;
  const isCommercial = normalizedPropertyType === "commercial";

  const kycComplete = isKycComplete({
    isCommercial,
    lastNameVerificationOk,
    isLastNameVerifyFailed,
    drivingLicenseFile,
    formationFile,
    authorizationFile,
  });

  const onVerifyLastName = useCallback(() => {
    void (async () => {
      try {
        setIsVerifyingLastName(true);
        const isValid = await verifyOwnerLastName(propertyId || "", lastName);
        setLastNameVerificationOk(isValid);
        setIsLastNameVerifyFailed(!isValid);
      } catch (e) {
        console.error(e);
        window.alert(
          e instanceof Error ? e.message : "Could not verify last name. Please try again."
        );
      } finally {
        setIsVerifyingLastName(false);
      }
    })();
  }, [propertyId, lastName]);

  const onDrivingLicenseChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setDrivingLicenseFile(file ?? null);
    setDrivingLicenseFileName(file?.name || "");
  }, []);

  const onFormationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFormationFile(file ?? null);
    setFormationFileName(file?.name || "");
  }, []);

  const onAuthorizationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setAuthorizationFile(file ?? null);
    setAuthorizationFileName(file?.name || "");
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!signature || !selectedPropertyType || !signatureConsentAccepted) return;

    setIsSubmitting(true);
    try {
      await submitContinueModalFlow({
        propertyId: propertyId || "",
        selectedPropertyType,
        signature,
        propertyRelationship,
      });
      setSuccessRedirectSeconds(7);
      setIsSuccessModalOpen(true);
    } catch (err) {
      console.error(err);
      window.alert(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [signature, selectedPropertyType, propertyId, propertyRelationship, signatureConsentAccepted]);

  return {
    visible,
    propertyId: propertyId || "",
    isCommercial,
    lastName,
    setLastName,
    isLastNameVerifyFailed,
    isVerifyingLastName,
    onVerifyLastName,
    drivingLicenseFileName,
    onDrivingLicenseChange,
    formationFileName,
    onFormationChange,
    authorizationFileName,
    onAuthorizationChange,
    signatureCanvasRef,
    startDrawing,
    draw,
    stopDrawing,
    clearSignature,
    signature,
    kycComplete,
    isSubmitting,
    isSuccessModalOpen,
    successRedirectSeconds,
    signatureConsentAccepted,
    setSignatureConsentAccepted,
    handleSubmit,
    onSuccessContinue: redirectToSupportingUpload,
    onClose,
  };
}
